/**
 * Declared-view addressing — the ONE lookup every headless surface shares
 * (MCP views, the server's view route, the CLI's `--view-name`; the tabular
 * and flow renderers both resolve through here).
 *
 * Order: the space-qualified spelling (`Space::Owner.Name`, v0.11 D2) first,
 * then a unique qualified path, then a unique bare name. A name shared by
 * twins — owner-scoped OR cross-space — is an explicit ambiguity listing the
 * unambiguous spellings, never doc-order first-find.
 */
import { resolveViewInstance, type ResolvedModel, type ViewDeclV2 } from "@archlang/engine";

/** A view's unambiguous spelling: `Space::Owner.Name` when it landed in a
 *  space (v0.11 D2), else the bare qualified `Owner.Name`. */
export function spelledViewName(v: ViewDeclV2): string {
  const qn = [...(v.ownerPath ?? []), v.name].join(".");
  return v.space && v.space.length > 0 ? `${v.space.join(".")}::${qn}` : qn;
}

export function lookupDeclaredView(
  model: ResolvedModel,
  name: string,
): { readonly ok: true; readonly view: ViewDeclV2 } | { readonly ok: false; readonly error: string } {
  const views = model.views ?? [];
  const qualifiedOf = (v: ViewDeclV2) => [...(v.ownerPath ?? []), v.name].join(".");
  const ambiguous = (cands: readonly ViewDeclV2[]) => ({
    ok: false as const,
    error: `view name "${name}" is ambiguous — qualify it: ${cands.map(spelledViewName).join(", ")}`,
  });
  let raw = views.find((v) => spelledViewName(v) === name);
  if (!raw) {
    // The bare qualified path — twins across spaces share it (v0.11).
    const qual = views.filter((v) => qualifiedOf(v) === name);
    if (qual.length > 1) return ambiguous(qual);
    raw = qual[0];
  }
  if (!raw) {
    const bare = views.filter((v) => v.name === name);
    if (bare.length > 1) return ambiguous(bare);
    raw = bare[0];
  }
  if (!raw) return { ok: false, error: `no view named "${name}" in the workspace` };
  return { ok: true, view: raw };
}

/**
 * Which surface a view renders as — the representation switch's decision, lifted
 * out of ViewerComposition so it's pure + gate-testable (and the component stays
 * presentational). A view carries at most one of table/matrix/grid/flow (grammar
 * §view); everything else (show/hide/group/style/on) projects onto the board.
 * Absent view → "board" (the default/identity surface).
 *
 * A view INSTANCE carries no representation clause of its own — the decision
 * resolves the parent chain (`resolveViewInstance`, the same merge every
 * surface renders), so an instance of a table/matrix/grid/flow-carrying parent
 * dispatches to the inherited surface instead of silently falling back to the
 * board. Callers with a workspace in hand pass `viewsByName`; without it the
 * raw clauses decide (an unresolvable parent is the board, matching the
 * projection's degrade).
 */
export type ViewRepresentation = "board" | "table" | "matrix" | "grid" | "index" | "flow";

export function viewRepresentation(
  view: ViewDeclV2 | undefined | null,
  viewsByName?: ReadonlyMap<string, ViewDeclV2>,
): ViewRepresentation {
  const effective =
    view?.instanceOf && viewsByName ? resolveViewInstance(view, viewsByName).view : view;
  if (effective?.clauses.some((c) => c.kind === "table")) return "table";
  if (effective?.clauses.some((c) => c.kind === "matrix")) return "matrix";
  if (effective?.clauses.some((c) => c.kind === "grid")) return "grid";
  if (effective?.clauses.some((c) => c.kind === "index")) return "index";
  if (effective?.clauses.some((c) => c.kind === "flow")) return "flow";
  return "board";
}
